import { useState, useCallback, useMemo } from 'react';
import { InvoiceRecord } from '../utils/invoiceUtils';

/* ─── Types ──────────────────────────────────────────────────────────────── */

export interface BasicInfo {
  contact_name?: string;
  contact_id?: number | string;
  activation_status?: string;
  created_date?: string;
}

export interface ProjectAssociationRaw {
  project_name?: string;
  name?: string;
  project_id?: number | string;
  entity_name?: string;
  billing_entity?: string;
  entity?: string;
  status?: string;
  [key: string]: unknown;
}

export interface ContactProfile {
  basic_info?: BasicInfo;
  project_associations?: unknown;
}

export interface FinancialMetrics {
  total_invoiced_usd?: number;
  total_paid_usd?: number;
  outstanding_usd?: number;
  total_cancelled_usd?: number;
  payment_ratio_percent?: number;
}

export interface DetailedInvoices {
  invoice_records?: InvoiceRecord[];
}

export interface Metadata {
  original_currency?: string;
}

export interface CurrencyAnalysis {
  primary_currency?: string;
}

export interface TimesheetEntry {
  year_month?: string;
  user?: string;
  total_hours?: number;
  billable_hours?: number;
  non_billable_hours?: number;
  entries?: number;
  [key: string]: unknown;
}

export interface ClientDetailData {
  metadata?: Metadata;
  currency_analysis?: CurrencyAnalysis;
  contact_profile?: ContactProfile;
  financial_metrics?: FinancialMetrics;
  detailed_invoices?: DetailedInvoices;
  timesheet_data?: TimesheetEntry[];
  project_timesheet?: TimesheetEntry[];
}

/* ─── Hook ───────────────────────────────────────────────────────────────── */

interface UseClientDetailResult {
  data: ClientDetailData | null;
  loading: boolean;
  error: string | null;
  loadClient: (filename: string) => Promise<void>;
  clearClient: () => void;
}

export const useClientDetail = (): UseClientDetailResult => {
  const [data, setData] = useState<ClientDetailData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadClient = useCallback(async (filename: string): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`/enhanced_cxo_data/${filename}`);
      if (!res.ok) throw new Error(`Failed: ${res.status}`);
      setData(await res.json());
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, []);

  const clearClient = useCallback(() => {
    setData(null);
    setError(null);
  }, []);

  return useMemo(
    () => ({ data, loading, error, loadClient, clearClient }),
    [data, loading, error, loadClient, clearClient],
  );
};
